import { useEffect, useState } from 'react';
import { StatusState } from '../../Hooks/useBizQuery';
import './biz-button.scss';
import { Link, useNavigate } from 'react-router-dom';
import { getIconClassByType, TBizIcon } from './BizIcon';

export type BtnIcon =
	| 'far fa-save'
	| 'far fa-check'
	| 'fal fa-exclamation-triangle'
	| 'fa-solid fa-plus'
	| 'fas fa-reply'
	| 'fad fa-spinner-third fa-spin'
	| 'far fa-upload'
	| 'far fa-eye'
	| 'fa-solid fa-arrow-left'
	| 'fa-solid fa-arrow-right';

type BizButtonProps = {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	text: string;
	icon?: BtnIcon;
	isLead?: boolean;
	style?: React.CSSProperties;
	state?: StatusState;
	legacy?: boolean;
	className?: string;
	disabled?: boolean;
	onlyShowIconOnLoading?: boolean;
};

export function BizButton({
	onClick,
	text,
	icon: pIcon,
	isLead,
	style,
	state,
	legacy,
	className,
	disabled,
	onlyShowIconOnLoading,
}: BizButtonProps) {
	const [internalState, setInternalState] = useState<StatusState>(state ?? 'INITIAL');

	useEffect(() => {
		if (state) {
			setInternalState(state);
		}
	}, [state]);

	useEffect(() => {
		if (internalState === 'SUCCESS' || internalState === 'ERROR') {
			const timer = setTimeout(() => {
				setInternalState('INITIAL');
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [internalState]);

	let classes = 'biz-button';
	if (legacy) {
		classes = 'admin-btn';
	}

	if (isLead) {
		classes += ' btn-lead';
	}

	if (className) {
		classes += ` ${className}`;
	}

	let icon = pIcon;

	if (internalState === 'LOADING') {
		icon = 'fad fa-spinner-third fa-spin';
	} else if (internalState === 'SUCCESS') {
		icon = 'far fa-check';
	} else if (internalState === 'ERROR') {
		icon = 'fal fa-exclamation-triangle';
	}

	if (legacy) {
		if (disabled) {
			classes += ' disabled';
		}

		return (
			<span className={classes} onClick={state === 'LOADING' ? undefined : onClick} style={style}>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		);
	}

	if (internalState === 'LOADING') {
		classes += ' loading';
	} else if (internalState === 'SUCCESS' && !onlyShowIconOnLoading) {
		classes += ' success';
	} else if (internalState === 'ERROR') {
		classes += ' error';
	}

	const isDisabled = state === 'LOADING' || disabled;

	if (onlyShowIconOnLoading && internalState !== 'LOADING') {
		icon = undefined;
	}

	return (
		<button className={classes} onClick={isDisabled ? undefined : onClick} disabled={isDisabled}>
			{icon !== undefined && (
				<div className='btn-icon-wrapper state-info'>
					<i className={icon} />
				</div>
			)}
			<span className='label'>{text}</span>
		</button>
	);
}

type BizLinkButtonProps = {
	text: string;
	link?: string;
	icon?: TBizIcon;
	isLead?: boolean;
	isBackBtn?: boolean;
	legacy?: boolean;
};

export function BizLinkBackButton({ text, icon, isLead, legacy }: Partial<Omit<BizLinkButtonProps, 'isBackBtn'>>) {
	return <BizLinkButton text={text || 'Back'} icon={icon || 'REPLY'} isLead={isLead} isBackBtn={true} legacy={legacy} />;
}

export function BizLinkButton({ link, text, icon: pIcon, isLead, isBackBtn, legacy }: BizLinkButtonProps) {
	const navigate = useNavigate();

	let classes = 'biz-button';
	if (legacy) {
		classes = 'admin-btn';
	}

	if (isLead) {
		classes += ' btn-lead';
	}

	const icon = pIcon ? getIconClassByType(pIcon) : undefined;

	if (isBackBtn) {
		return (
			<span className={classes} onClick={() => navigate(-1)}>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		);
	}

	if (!link) {
		return null;
	}

	return (
		<Link to={link}>
			<span className={classes}>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		</Link>
	);
}
